import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronUp } from "lucide-react"
import { CrewMemberTacho, LegalCounters } from "@/reducers/tachosSlice"
import { hasLegalCounters } from "@/lib/legalCountersParser"
import { LegalCountersDialog } from "./LegalCountersDialog"
import { useTranslation } from "react-i18next"
import { formatHoursMinutes } from "@/lib/i18n/format"

interface TimeProgressBarProps {
  usedSecondsToday: number
  remainingSecondsToday: number
  remainingSecondsThisWeek: number | null
  remainingWeeklyRestSeconds: number | null
  remainingSecondsTwoWeeks: number | null
  remainingSecondsOfUninterruptedDriving: number | null
  dayEndTimestamp?: string | null
  remainingDailyExtensions?: number | null
  isDoubleCrew?: boolean
  // The provider's full legal-counter panel (Transics), per card
  driverName?: string | null
  legalCounters?: LegalCounters | null
  crew?: CrewMemberTacho[]
  timestamp?: string | null
}

export const TimeProgressBar: React.FC<TimeProgressBarProps> = ({
  usedSecondsToday,
  remainingSecondsToday,
  remainingSecondsThisWeek,
  remainingWeeklyRestSeconds,
  remainingSecondsTwoWeeks,
  remainingSecondsOfUninterruptedDriving,
  dayEndTimestamp,
  remainingDailyExtensions,
  isDoubleCrew = false,
  driverName,
  legalCounters,
  crew,
  timestamp,
}) => {
  const { t } = useTranslation()
  const [showTwoWeeksData, setShowTwoWeeksData] = useState(false)
  const showLegalCounters = hasLegalCounters(legalCounters, crew)

  const totalSeconds = usedSecondsToday + remainingSecondsToday
  const usedRatio = usedSecondsToday / totalSeconds
  const formatTime = (seconds: number): string => formatHoursMinutes(t, seconds)

  const haveTwoWeeksData = remainingSecondsTwoWeeks !== null
  const haveThisWeekData = remainingSecondsThisWeek !== null
  const haveWeeklyRestData = remainingWeeklyRestSeconds !== null
  const haveDayEndData =
    dayEndTimestamp !== null && dayEndTimestamp !== undefined
  const usedSecondTwoWeeks = haveTwoWeeksData
    ? 90 * 60 * 60 - remainingSecondsTwoWeeks!
    : 0

  const usedTWoWeeksRatio = haveTwoWeeksData
    ? usedSecondTwoWeeks / (usedSecondTwoWeeks + remainingSecondsTwoWeeks!)
    : 0

  const showBreakInfo =
    !!remainingSecondsOfUninterruptedDriving &&
    remainingSecondsOfUninterruptedDriving > 0

  const breakText = showBreakInfo
    ? t("tacho.breakRequired", {
        time: formatTime(remainingSecondsOfUninterruptedDriving),
      })
    : ""
  const breakRatio = showBreakInfo
    ? remainingSecondsOfUninterruptedDriving / remainingSecondsToday
    : 0

  return (
    <div
      className={`flex w-full flex-1 bg-sidebar ${!haveTwoWeeksData ? "h-16" : ""}`}
    >
      <div className={`h-full w-full min-w-[300px] flex-1 px-4 py-2`}>
        <div className="mb-1 flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-accent-foreground">
              {formatTime(usedSecondsToday)}
            </span>
            <span className="text-xs text-muted-foreground">
              {" "}
              {t("tacho.done")} {breakText}
            </span>
            {isDoubleCrew && (
              <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                {t("tacho.doubleCrew")}
              </span>
            )}
          </div>
          <div className="text-right">
            <span className="text-xs font-medium text-accent-foreground">
              {formatTime(remainingSecondsToday)}
            </span>
            <span className="text-xs text-muted-foreground">
              {" "}
              {t("tacho.left")}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-y-1">
          <div className="flex w-full flex-1">
            <div
              className="h-2 rounded-l-full bg-blue-500"
              style={{ width: `${usedRatio * 100}%` }}
            />
            <div
              className="relative h-2 rounded-r-full bg-gray-300"
              style={{ width: `${100 - usedRatio * 100}%` }}
            >
              {showBreakInfo && (
                <div
                  style={{
                    left: `${breakRatio * 100}%`,
                  }}
                  className="absolute h-2 w-2 rotate-[45deg] scale-50 bg-fuchsia-700"
                />
              )}
            </div>
          </div>
          {haveDayEndData && (
            <div className="mt-2 text-xs text-muted-foreground">
              {t("tacho.workDayEndsAt")}{" "}
              <span className="text-accent-foreground">{dayEndTimestamp}</span>
            </div>
          )}
          {remainingDailyExtensions !== null &&
            remainingDailyExtensions !== undefined && (
              <div className="mt-1 text-xs text-muted-foreground">
                {remainingDailyExtensions === 0
                  ? t("tacho.dailyLimitExhausted")
                  : t("tacho.dailyLimitExtended", {
                      count: remainingDailyExtensions,
                    })}
              </div>
            )}
        </div>
        {haveTwoWeeksData && showTwoWeeksData && (
          <>
            {haveThisWeekData && (
              <div className="my-2 flex items-center">
                <span className="text-xs font-medium text-accent-foreground">
                  {formatTime(remainingSecondsThisWeek)}
                </span>
                <span className="ml-1 text-xs text-muted-foreground">
                  {t("tacho.leftThisWeek")}
                </span>
              </div>
            )}
            {haveWeeklyRestData && (
              <div className="my-2 text-xs text-muted-foreground">
                {t("tacho.nextWeekendBreak")}{" "}
                <span className="font-medium text-accent-foreground">
                  {Math.round(remainingWeeklyRestSeconds / 3600)}h
                </span>
              </div>
            )}
            <div className="my-2 flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-accent-foreground">
                  {formatTime(usedSecondTwoWeeks)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {" "}
                  {t("tacho.drivenTwoWeeks")}
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium dark:text-gray-100">
                  {formatTime(remainingSecondsTwoWeeks!)}
                </span>
                <span className="text-xs"> {t("tacho.leftOfTwoWeeks")}</span>
              </div>
            </div>
            <div className="flex w-full flex-1">
              <div
                className="h-2 rounded-l-full bg-blue-500"
                style={{ width: `${usedTWoWeeksRatio * 100}%` }}
              />
              <div
                className="h-2 rounded-r-full bg-gray-300"
                style={{ width: `${100 - usedTWoWeeksRatio * 100}%` }}
              />
            </div>
          </>
        )}
      </div>
      {(haveTwoWeeksData || showLegalCounters) && (
        <div className="flex flex-col gap-1 py-4 pr-4">
          {haveTwoWeeksData && (
            <Button
              className="h-8 w-8"
              onClick={() => setShowTwoWeeksData(!showTwoWeeksData)}
              variant="outline"
            >
              {showTwoWeeksData ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>
          )}
          {showLegalCounters && (
            <LegalCountersDialog
              driverName={driverName}
              legalCounters={legalCounters}
              crew={crew}
              timestamp={timestamp}
            />
          )}
        </div>
      )}
    </div>
  )
}
